import React, { useContext } from 'react';
import numeral from 'numeral';
import { createColumnHelper } from '@tanstack/react-table';
import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { UserImportsContext } from '../context';
import { ActionMenu } from 'components/_atoms';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import ListSection from 'components/_layouts/ListSection';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import datetimeHelper from 'helpers/datetimeHelper';
import { Job } from '../../_types';
import userImportHelper from '../userImportHelper';

const columnHelper = createColumnHelper<Job>();

const UserImportsList: React.FC = () => {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { userImports, userImportsQuery } = useContext(UserImportsContext);

  const renderCountBadges = (data: any) => {
    const badges: React.ReactNode[] = [];
    const counts = userImportHelper.userImportCounts(data);
    Object.entries(counts).forEach(([k, v]) => {
      badges.push(
        <PhxBadge
          key={`count_badge_${k}`}
          color={k === 'FAIL' ? 'error' : 'info'}
        >
          {`${t(`userImports.counts.${k}`)}: ${numeral(v).format('0,0')}`}
        </PhxBadge>
      );
    });
    return badges;
  };

  const columns = [
    columnHelper.accessor('jobDescr', {
      cell: (info) => (
        <Stack>
          <Stack
            direction='row'
            spacing={4}
          >
            <Link href={`./${info.row.original.jobId}`}>
              <Typography
                variant='h6'
                sx={{ fontWeight: 'semibold', display: 'inline-flex' }}
              >
                {info.row.original.jobDescr}
              </Typography>
            </Link>
          </Stack>
          <Box sx={{ display: 'inline-flex' }}>
            <Tooltip title={datetimeHelper.formatDateTime(info.row.original.createdAt)}>
              <Typography>{datetimeHelper.formatDate(info.row.original.createdAt)}</Typography>
            </Tooltip>
          </Box>
        </Stack>
      ),
      header: () => t('users.properties.name'),
      size: 9999,
    }),
    columnHelper.display({
      cell: (info) => (
        <Stack
          direction='row'
          spacing={2}
        >
          {renderCountBadges(info.row.original)}
        </Stack>
      ),
      header: t('userImports.properties.userCount'),
      size: 9999,
    }),
    columnHelper.accessor('status', {
      cell: (info) => userImportHelper.renderStatusBadge(info.row.original),
      header: () => t('userImports.properties.status'),
      size: 200,
    }),
    ...(currentOrganizationId && hasPermission('updateUserImport', currentOrganizationId)
      ? [
          columnHelper.display({
            id: 'actions',
            cell: (info) => (
              <ActionMenu
                id={`action_menu_${info.row.original.jobId}`}
                items={[
                  {
                    key: `edit_${info.row.original.jobId}`,
                    label: t('common.edit'),
                    href: `./edit/${info.row.original.jobId}`,
                  },
                ]}
              />
            ),
            header: () => '',
            size: 60,
          }),
        ]
      : []),
  ];

  const createButton =
    currentOrganizationId && hasPermission('importUserFile', currentOrganizationId) ? (
      <Stack
        direction='row'
        spacing={2}
      >
        <Button
          variant='contained'
          size='small'
          href='./new'
        >
          {t('userImports.actions.create')}
        </Button>
      </Stack>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: t('userImports.title'),
        actions: createButton,
      }}
    >
      <Stack spacing={4}>
        <ListSection
          columns={columns}
          data={userImports || []}
          query={userImportsQuery}
          showChrome={true}
        />
      </Stack>
    </CommonLayout>
  );
};

export default UserImportsList;
